import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ETIQUETA_DE_MODULO } from '@/components/layout/modulos';

/**
 * La píldora del módulo («Compite · Decisiones»). 19-oct (rediseño de toda la
 * app): rellena con el color de Compite, como las de Cotiza y Comercia, para que
 * el módulo se reconozca igual en todas las pantallas.
 */
export function PildoraDeModulo({
  seccion,
  icono: Icono,
  className,
}: {
  seccion: string;
  icono: LucideIcon;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-2 rounded-full py-1 pl-1 pr-3.5 text-sm font-semibold',
        ETIQUETA_DE_MODULO.compite,
        className
      )}
    >
      <span className="flex h-6 w-6 items-center justify-center rounded-full bg-black/15">
        <Icono className="h-3.5 w-3.5" aria-hidden="true" />
      </span>
      Compite · {seccion}
    </span>
  );
}

interface EncabezadoCompiteProps {
  /** La sección dentro de Compite («Decisiones», «Estrategia»): va en la píldora. */
  seccion: string;
  icono: LucideIcon;
  titulo: ReactNode;
  subtitulo?: ReactNode;
  /** El botón principal y los secundarios, a la derecha. */
  acciones?: ReactNode;
  /** Algo encima de la píldora (el «← Decisiones de hoy»). */
  arriba?: ReactNode;
  /**
   * Sin la píldora del módulo: en una pantalla de segundo nivel (Revisar
   * precios) el «← volver» ya dice dónde se está, como en la maqueta.
   */
  sinPildora?: boolean;
  className?: string;
}

/**
 * El encabezado de una pantalla de Compite: la píldora del módulo, el título
 * grande y una línea que dice para qué sirve la pantalla. A la derecha, como
 * mucho un botón principal (sistema visual de la maqueta).
 */
export function EncabezadoCompite({
  seccion,
  icono,
  titulo,
  subtitulo,
  acciones,
  arriba,
  sinPildora = false,
  className,
}: EncabezadoCompiteProps) {
  return (
    <header className={cn('flex flex-wrap items-end justify-between gap-x-6 gap-y-4', className)}>
      <div className="min-w-0 space-y-3">
        {/* Cada uno en su renglón: son `inline-flex` y, sueltos, quedaban pegados. */}
        {arriba && <div>{arriba}</div>}
        {!sinPildora && (
          <div>
            <PildoraDeModulo seccion={seccion} icono={icono} />
          </div>
        )}
        {/* Como el resto de la app: Poppins 600 a 34 px (la 800 no se carga y se inventaba). */}
        <h1 className="text-[34px] font-semibold leading-[1.1] tracking-[-0.025em] text-foreground">
          {titulo}
        </h1>
        {subtitulo && (
          <p className="max-w-2xl text-base leading-relaxed text-muted-foreground">{subtitulo}</p>
        )}
      </div>
      {acciones && <div className="flex flex-wrap items-center gap-2">{acciones}</div>}
    </header>
  );
}
